// The endpoint map tab: the folder's HTTP endpoints, and for the one picked, every file, member and
// EF Core entity its handler reaches.
import { endpointReach, type CodeEndpoint } from '@modelviewer/core';
import { Loader2, Route, Search, Shuffle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Canvas, Divider, ToolButton, type CanvasSelection, type CanvasTarget, type Vocabulary } from '../diagram/Canvas';
import { useCardFonts } from '../diagram/DiagramView';
import { measureText } from '../diagram/measure';
import { layoutInput, useLayout } from '../diagram/useLayout';
import { cn } from '../lib/cn';
import { loadCodeMap, selectCode, showEndpoint, useCodeMap } from '../state/codemap';
import { useWorkspace } from '../state/workspace';
import { codeIndex } from './codeIndex';
import { buildEndpointGraph, ENDPOINT_CARD } from './endpointGraph';
import { useCodeSettings } from './settings';

const MODES: Vocabulary['modes'] = [
  { value: 'all', label: 'Members', title: 'Every member the endpoint reaches' },
  { value: 'none', label: 'Types', title: 'Only the types with something reached' },
];

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-added-soft text-added',
  POST: 'bg-accent-soft text-accent',
  PUT: 'bg-changed-soft text-changed',
  PATCH: 'bg-changed-soft text-changed',
  DELETE: 'bg-removed-soft text-removed',
};

export function MethodBadges({ endpoint }: { endpoint: CodeEndpoint }) {
  const methods = endpoint.methods.length ? endpoint.methods : ['ANY'];
  return (
    <span className="flex flex-none gap-0.5">
      {methods.map((m) => (
        <span key={m} className={cn('rounded px-1 font-mono text-[10.5px] leading-4 font-semibold', METHOD_COLORS[m] ?? 'bg-active text-muted')}>
          {m}
        </span>
      ))}
    </span>
  );
}

/** The endpoint the map shows: the one picked, or the first. */
export function useCurrentEndpoint(): CodeEndpoint | null {
  const endpoints = useCodeMap((s) => s.map?.endpoints);
  const picked = useCodeMap((s) => s.endpoint);
  return endpoints?.find((e) => e.id === picked) ?? endpoints?.[0] ?? null;
}

function EndpointList({ endpoints, current }: { endpoints: CodeEndpoint[]; current: CodeEndpoint | null }) {
  const [filter, setFilter] = useState('');
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? endpoints.filter((e) => e.id.toLowerCase().includes(q)) : endpoints;
  }, [endpoints, filter]);
  return (
    <aside className="flex w-72 flex-none flex-col border-r border-border bg-panel @max-[44rem]:hidden" aria-label="Endpoints">
      <div className="flex h-8 items-center gap-1.5 px-3 text-[11px] font-semibold tracking-wider text-faint uppercase">
        Endpoints <span className="ml-auto font-normal tracking-normal normal-case">{endpoints.length}</span>
      </div>
      <label className="mx-2 mb-1 flex items-center gap-1.5 rounded-md border border-border px-2">
        <Search size={12} className="text-faint" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter routes"
          aria-label="Filter routes"
          className="h-6 min-w-0 flex-1 bg-transparent text-[12px] outline-none placeholder:text-faint pointer-coarse:h-9"
        />
      </label>
      <div className="min-h-0 flex-1 overflow-y-auto px-1.5 pb-2" role="listbox" aria-label="Endpoint">
        {shown.map((e) => (
          <button
            key={e.id}
            type="button"
            role="option"
            aria-selected={current?.id === e.id}
            onClick={() => showEndpoint(e.id)}
            title={`${e.id}\n${e.file}:${e.line}`}
            className={cn(
              'flex w-full items-center gap-2 rounded-md px-2 py-1 text-left pointer-coarse:py-2',
              current?.id === e.id ? 'bg-active text-text' : 'text-muted hover:bg-hover hover:text-text',
            )}
          >
            <MethodBadges endpoint={e} />
            <span className="min-w-0 flex-1 truncate font-mono text-[12px]">{e.route}</span>
          </button>
        ))}
      </div>
    </aside>
  );
}

export function EndpointMapView() {
  const map = useCodeMap((s) => s.map);
  const loading = useCodeMap((s) => s.loading);
  const error = useCodeMap((s) => s.error);
  const source = useWorkspace((s) => s.source);
  const settings = useCodeSettings();
  const fontsReady = useCardFonts();
  const current = useCurrentEndpoint();

  useEffect(() => {
    if (!useCodeMap.getState().map && !useCodeMap.getState().loading) void loadCodeMap();
  }, [source]);

  const reach = useMemo(() => (map && current ? endpointReach(map, current.id, { guesses: settings.guesses }) : null), [map, current, settings.guesses]);
  const graph = useMemo(() => (map && reach && fontsReady ? buildEndpointGraph(map, reach, { members: settings.endpointMembers, measure: measureText }) : null), [map, reach, fontsReady, settings.endpointMembers]);
  const input = useMemo(() => (graph ? layoutInput(graph) : null), [graph]);
  const layoutKey = source && current ? `${source.id}|endpoint|${current.id}|${settings.endpointMembers}` : null;
  const { positions, save, relayout } = useLayout(layoutKey, input);

  const selection = useCodeMap((s) => s.selection);
  const selected = useMemo<CanvasSelection>(() => {
    if (!selection || !map) return { node: null, row: null, edge: null };
    if (selection.kind === 'file') return { node: selection.path, row: null, edge: null };
    if (selection.kind === 'edge') return { node: null, row: null, edge: selection.id };
    const file = codeIndex(map).fileOf(selection.id);
    return { node: null, row: file ? `${file}::${selection.id}` : null, edge: null };
  }, [selection, map]);

  const onSelect = useCallback((target: CanvasTarget | null) => {
    if (!target) selectCode(null);
    else if (target.kind === 'node') selectCode(target.node.id === ENDPOINT_CARD ? null : { kind: 'file', path: target.node.key });
    else if (target.kind === 'row') selectCode(target.row.id.startsWith('data:') ? null : { kind: 'member', id: target.row.id });
    else selectCode(target.edge.id === 'handler' ? null : { kind: 'edge', id: target.edge.id });
  }, []);

  const request = useCodeMap((s) => s.reveal);
  const reveal = useMemo(() => (request?.view === 'endpoints' ? { id: request.path, nonce: request.nonce, focus: request.focus } : null), [request]);
  const vocabulary = useMemo<Vocabulary>(
    () => ({ search: 'Find file or member', modesLabel: 'What to show', modes: MODES, mode: settings.endpointMembers, setMode: settings.setEndpointMembers, joins: false }),
    [settings.endpointMembers, settings.setEndpointMembers],
  );

  const endpoints = map?.endpoints ?? [];
  let body: React.ReactNode;
  if (error) body = <div className="flex h-full items-center justify-center p-6 text-center text-muted">{error}</div>;
  else if (map && !endpoints.length) {
    body = (
      <div className="flex h-full flex-col items-center justify-center gap-1 p-6 text-center text-muted">
        <p>No HTTP endpoints found in this folder.</p>
        <p className="max-w-md text-[12px] text-faint">The endpoint map looks for ASP.NET Core controllers (classes with [ApiController] or deriving from ControllerBase) and minimal APIs (app.MapGet, MapPost and the like, with MapGroup prefixes).</p>
      </div>
    );
  } else if (!graph || !positions || !reach || !current) {
    body = (
      <div className="flex h-full items-center justify-center gap-2 text-muted">
        <Loader2 size={16} className="animate-spin" />
        {loading ? `Reading code… ${loading.read} of ${loading.total} files` : 'Laying out…'}
      </div>
    );
  } else {
    const members = reach.depth.size;
    body = (
      <Canvas
        graph={graph}
        positions={positions}
        selected={selected}
        onSelect={onSelect}
        onMoved={save}
        onRelayout={() => void relayout()}
        fitKey={`endpoint|${source?.id}|${current.id}`}
        reveal={reveal}
        vocabulary={vocabulary}
        tools={
          <>
            <Divider />
            <ToolButton label={settings.guesses ? 'Do not follow guessed calls' : 'Follow guessed calls too'} active={settings.guesses} onClick={() => settings.toggle('guesses')}>
              <Shuffle size={15} />
            </ToolButton>
          </>
        }
        status={
          <span data-testid="endpoint-status">
            {reach.files.length} {reach.files.length === 1 ? 'file' : 'files'} · {members} {members === 1 ? 'member' : 'members'}{reach.data.length > 0 && ` · ${reach.data.length} ${reach.data.length === 1 ? 'entity' : 'entities'}`}
            {reach.truncated && ' · stopped early, there is more'}
          </span>
        }
      />
    );
  }

  return (
    <div className="@container flex h-full">
      {endpoints.length > 0 && <EndpointList endpoints={endpoints} current={current} />}
      <div className="flex min-w-0 flex-1 flex-col">
        {endpoints.length > 0 && (
          <label className="flex flex-none items-center gap-2 border-b border-border bg-panel px-2 py-1.5 @min-[44rem]:hidden">
            <Route size={14} className="flex-none text-faint" />
            <select
              value={current?.id ?? ''}
              onChange={(e) => showEndpoint(e.target.value)}
              aria-label="Endpoint"
              className="h-8 min-w-0 flex-1 rounded-md border border-border bg-bg px-2 font-mono text-[16px] text-text md:text-[12.5px]"
            >
              {endpoints.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.id}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="min-h-0 flex-1">{body}</div>
      </div>
    </div>
  );
}
